import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  ClipboardList, CheckSquare, FileText, Shield, Loader2, X, Plus, Trash2, Save, Edit3,
  Copy
} from 'lucide-react';

interface TaskTemplate {
  id: string;
  biz_type: string;
  template_name: string;
  template_category: 'intake' | 'service' | 'cleanup' | 'safety';
  fields: Array<{
    name: string;
    label: string;
    type: string;
    required?: boolean;
    options?: string[];
  }>;
  checklist_items: Array<{
    id: string;
    text: string;
    completed: boolean;
    step?: number;
  }>;
  protective_data_points: Record<string, boolean>;
  preset_quote_items?: Array<{
    description: string;
    unit: string;
    quantity: number;
    price: number;
  }>;
  is_active?: boolean;
}

interface FieldValue {
  [key: string]: string | number | boolean | string[];
}

export function TaskTemplateEngine() {
  const { config } = useBusinessConfig();
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTemplate, setActiveTemplate] = useState<TaskTemplate | null>(null);
  const [fieldValues, setFieldValues] = useState<FieldValue>({});
  const [checklistState, setChecklistState] = useState<Record<string, boolean>>({});
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);
  const [customChecklist, setCustomChecklist] = useState<Array<{ id: string; text: string; completed: boolean }>>([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  
  // Template Builder State
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderStep, setBuilderStep] = useState<'details' | 'checklist' | 'fields' | 'pricing'>('details');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateCategory, setNewTemplateCategory] = useState<'intake' | 'service' | 'cleanup' | 'safety'>('service');
  const [newChecklistItems, setNewChecklistItems] = useState<Array<{ id: string; text: string; step: number }>>([]);
  const [newFields, setNewFields] = useState<TaskTemplate['fields']>([]);
  const [newQuoteItems, setNewQuoteItems] = useState<TaskTemplate['preset_quote_items']>([]);
  const [newChecklistInput, setNewChecklistInput] = useState('');
  const [currentStepNum, setCurrentStepNum] = useState(1);

  // Load templates for business type
  useEffect(() => {
    console.log('TaskTemplateEngine: config changed', config);
    loadTemplates();
  }, [config?.biz_type]);

  const loadTemplates = async () => {
    if (!config?.biz_type) {
      console.log('TaskTemplateEngine: No biz_type, skipping load');
      return;
    }

    setLoading(true);
    try {
      console.log('TaskTemplateEngine: Loading templates for biz_type:', config.biz_type);
      const { data, error } = await supabase
        .from('task_templates')
        .select('*')
        .eq('biz_type', config.biz_type)
        .eq('is_active', true)
        .order('template_category', { ascending: true });

      if (error) {
        console.error('TaskTemplateEngine: Template fetch error:', error);
        throw error;
      }
      console.log('TaskTemplateEngine: Loaded templates:', data?.length || 0);
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load task templates');
    } finally {
      setLoading(false);
    }
  };

  const openTemplate = (template: TaskTemplate) => {
    setActiveTemplate(template);
    setFieldValues({});
    setChecklistState({});
    // Initialize custom checklist from template
    setCustomChecklist(template.checklist_items.map(item => ({ ...item, completed: false })));
    setIsCreatingCustom(false);
  };

  const handleFieldChange = (fieldName: string, value: string | number | boolean | string[]) => {
    setFieldValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const addCustomChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    const newItem = {
      id: `custom-${Date.now()}`,
      text: newChecklistItem,
      completed: false,
    };
    setCustomChecklist(prev => [...prev, newItem]);
    setNewChecklistItem('');
  };

  const removeCustomChecklistItem = (id: string) => {
    setCustomChecklist(prev => prev.filter(item => item.id !== id));
  };

  const toggleCustomChecklistItem = (id: string) => {
    setCustomChecklist(prev => prev.map(item => 
      item.id === id ? { ...item, completed: !item.completed } : item
    ));
  };

  const saveTemplateData = async () => {
    if (!activeTemplate) return;

    try {
      const { error } = await supabase
        .from('audit_logs')
        .insert([{
          event_type: 'task_template_completed',
          meta: {
            template_id: activeTemplate.id,
            template_name: activeTemplate.template_name,
            biz_type: config?.biz_type,
            fields: fieldValues,
            checklist: checklistState,
            custom_checklist: customChecklist,
            protective_data: activeTemplate.protective_data_points,
            completed_at: new Date().toISOString(),
          }
        }]);

      if (error) throw error;
      toast.success(`${activeTemplate.template_name} saved successfully!`);
      setActiveTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template data');
    }
  };

  // ============ TEMPLATE BUILDER FUNCTIONS ============
  
  const startNewTemplate = () => {
    setShowBuilder(true);
    setBuilderStep('details');
    setNewTemplateName('');
    setNewTemplateCategory('service');
    setNewChecklistItems([]);
    setNewFields([]);
    setNewQuoteItems([]);
    setCurrentStepNum(1);
  };

  const addChecklistItem = () => {
    if (!newChecklistInput.trim()) return;
    setNewChecklistItems(prev => [...prev, {
      id: `item-${Date.now()}`,
      text: newChecklistInput.trim(),
      step: currentStepNum
    }]);
    setNewChecklistInput('');
  };

  const removeChecklistItem = (id: string) => {
    setNewChecklistItems(prev => prev.filter(item => item.id !== id));
  };

  const addStep = () => {
    setCurrentStepNum(prev => prev + 1);
  };

  const removeStep = (stepNum: number) => {
    if (stepNum === 1) return; // Can't remove first step
    setNewChecklistItems(prev => prev.filter(item => item.step !== stepNum));
    setCurrentStepNum(prev => Math.max(1, prev - 1));
  };

  const addField = (fieldType: string) => {
    const fieldName = `field_${newFields.length + 1}`;
    setNewFields(prev => [...prev, {
      name: fieldName,
      label: `${fieldType} field`,
      type: fieldType,
      required: false
    }]);
  };

  const updateField = (index: number, updates: Partial<TaskTemplate['fields'][0]>) => {
    setNewFields(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  };

  const removeField = (index: number) => {
    setNewFields(prev => prev.filter((_, i) => i !== index));
  };

  const addQuoteItem = () => {
    setNewQuoteItems(prev => [...(prev || []), {
      description: 'New service item',
      unit: 'job',
      quantity: 1,
      price: 0
    }]);
  };

  const updateQuoteItem = (index: number, updates: Partial<{ description: string; unit: string; quantity: number; price: number }>) => {
    setNewQuoteItems(prev => prev?.map((item, i) => i === index ? { ...item, ...updates } : item));
  };

  const removeQuoteItem = (index: number) => {
    setNewQuoteItems(prev => prev?.filter((_, i) => i !== index));
  };

  const saveNewTemplate = async () => {
    if (!newTemplateName || !config?.biz_type) {
      toast.error('Template name and business type required');
      return;
    }

    try {
      const templateData = {
        biz_type: config.biz_type,
        template_name: newTemplateName,
        template_category: newTemplateCategory,
        fields: newFields.length > 0 ? newFields : [],
        checklist_items: newChecklistItems.map(item => ({
          id: item.id,
          text: item.text,
          completed: false,
          step: item.step
        })),
        preset_quote_items: newQuoteItems && newQuoteItems.length > 0 ? newQuoteItems : [],
        protective_data_points: {},
        is_active: true
      };

      console.log('Saving template:', templateData);

      const { error } = await supabase.from('task_templates').insert([templateData]);

      if (error) {
        console.error('Template save error:', error);
        throw error;
      }
      
      toast.success('Template created!');
      setShowBuilder(false);
      loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error(`Failed to create: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const duplicateTemplate = async (template: TaskTemplate) => {
    try {
      const { error } = await supabase.from('task_templates').insert([{
        ...template,
        id: undefined,
        template_name: `${template.template_name} (Copy)`,
        is_active: true
      }]);

      if (error) throw error;
      toast.success('Template duplicated!');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const toggleTemplateActive = async (template: TaskTemplate) => {
    try {
      const { error } = await supabase
        .from('task_templates')
        .update({ is_active: !template.is_active })
        .eq('id', template.id);

      if (error) throw error;
      loadTemplates();
      toast.success(`Template ${template.is_active ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const getCompletionPercentage = () => {
    if (!activeTemplate) return 0;
    const totalItems = activeTemplate.checklist_items.length;
    if (totalItems === 0) return 0;
    const completedItems = Object.values(checklistState).filter(Boolean).length;
    return Math.round((completedItems / totalItems) * 100);
  };

  const renderField = (field: TaskTemplate['fields'][0]) => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            placeholder={field.label}
            value={(fieldValues[field.name] as string) || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none"
            required={field.required}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            placeholder={field.label}
            value={(fieldValues[field.name] as number) || ''}
            onChange={(e) => handleFieldChange(field.name, parseFloat(e.target.value) || 0)}
            className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none"
            required={field.required}
          />
        );
      case 'textarea':
        return (
          <textarea
            placeholder={field.label}
            value={(fieldValues[field.name] as string) || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="w-full h-32 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none resize-none"
            required={field.required}
          />
        );
      case 'select':
        return (
          <select
            value={(fieldValues[field.name] as string) || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none"
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'multiselect':
        const currentValue = (fieldValues[field.name] as string[]) || [];
        return (
          <div className="space-y-2">
            {field.options?.map(opt => (
              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentValue.includes(opt)}
                  onChange={(e) => {
                    const newValue = e.target.checked
                      ? [...currentValue, opt]
                      : currentValue.filter(v => v !== opt);
                    handleFieldChange(field.name, newValue);
                  }}
                  className="w-4 h-4"
                />
                <span className="text-sm font-bold text-slate-700">{opt}</span>
              </label>
            ))}
          </div>
        );
      case 'boolean':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={(fieldValues[field.name] as boolean) || false}
              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm font-bold text-slate-700">{field.label}</span>
          </label>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 space-y-4">
        <Loader2 className="animate-spin text-purple-600 w-12 h-12" />
        <p className="font-black uppercase italic tracking-widest text-slate-400 text-xs">Loading Task Templates...</p>
      </div>
    );
  }

  // Template Detail View
  if (activeTemplate) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-purple-600" />
            <h2 className="text-2xl font-black uppercase">{activeTemplate.template_name}</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveTemplate(null)}
            className="rounded-xl"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Progress Bar */}
        {activeTemplate.checklist_items.length > 0 && (
          <div className="bg-slate-100 rounded-2xl p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-black uppercase text-slate-500">Progress</span>
              <span className="text-sm font-black text-purple-600">{getCompletionPercentage()}%</span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-600 to-teal-500 transition-all duration-300"
                style={{ width: `${getCompletionPercentage()}%` }}
              />
            </div>
          </div>
        )}

        {/* Protective Data Points */}
        {Object.keys(activeTemplate.protective_data_points).length > 0 && (
          <Card className="border-2 border-amber-200 bg-amber-50 rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Shield className="w-5 h-5" />
                <span className="font-black uppercase text-sm">Protective Data Points</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(activeTemplate.protective_data_points).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <CheckSquare className={`w-4 h-4 ${value ? 'text-green-600' : 'text-slate-400'}`} />
                    <span className="text-xs font-bold text-slate-700 capitalize">
                      {key.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form Fields */}
        {activeTemplate.fields.length > 0 && (
          <Card className="border-2 border-slate-100 rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <FileText className="w-5 h-5" />
                <span className="font-black uppercase text-sm">Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {activeTemplate.fields.map(field => (
                <div key={field.name}>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Checklist */}
        <Card className="border-2 border-slate-100 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-slate-900">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5" />
                <span className="font-black uppercase text-sm">Checklist</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCreatingCustom(!isCreatingCustom)}
                className="text-xs font-bold"
              >
                <Edit3 className="w-3 h-3 mr-1" />
                {isCreatingCustom ? 'Done' : 'Customize'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Custom Checklist Builder */}
            {isCreatingCustom && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2 mb-4">
                <p className="text-xs font-bold text-slate-600">Add Custom Steps</p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Add a custom step..."
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomChecklistItem()}
                    className="h-10 rounded-xl"
                  />
                  <Button onClick={addCustomChecklistItem} size="sm" className="rounded-xl">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {customChecklist.filter(i => i.id.startsWith('custom-')).length > 0 && (
                  <div className="space-y-1 mt-2">
                    <p className="text-[10px] font-bold text-slate-500 uppercase">Your Custom Steps:</p>
                    {customChecklist.filter(i => i.id.startsWith('custom-')).map(item => (
                      <div key={item.id} className="flex items-center justify-between bg-white p-2 rounded-lg border border-slate-200">
                        <span className="text-sm font-bold text-slate-700">{item.text}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomChecklistItem(item.id)}
                          className="h-6 w-6 p-0 text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Checklist Items */}
            {customChecklist.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No checklist items. Customize to add your own!</p>
            )}
            {customChecklist.map(item => (
              <label
                key={item.id}
                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  item.completed
                    ? 'border-green-200 bg-green-50'
                    : 'border-slate-100 bg-white hover:border-purple-200'
                }`}
              >
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => toggleCustomChecklistItem(item.id)}
                  className="w-5 h-5 mt-0.5"
                />
                <span className={`text-sm font-bold flex-1 ${
                  item.completed ? 'text-green-800' : 'text-slate-700'
                }`}>
                  {item.text}
                </span>
                {item.id.startsWith('custom-') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCustomChecklistItem(item.id)}
                    className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </label>
            ))}
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={saveTemplateData}
          className="w-full h-14 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase tracking-widest"
        >
          <Save className="w-5 h-5 mr-2" />
          Save Template Data
        </Button>

        {/* Preset Quote Items */}
        {activeTemplate.preset_quote_items && activeTemplate.preset_quote_items.length > 0 && (
          <Card className="border-2 border-teal-200 bg-teal-50 rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-teal-800 text-sm">
                <ClipboardList className="w-5 h-5" />
                <span className="font-black uppercase">Quick-Add Quote Items</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-teal-700 mb-3">
                These items can be quickly added to quotes when using this template
              </p>
              <div className="grid gap-2">
                {activeTemplate.preset_quote_items.map((item: any, idx: number) => (
                  <div key={idx} className="bg-white p-3 rounded-xl border border-teal-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-bold text-slate-900">{item.description}</p>
                        <p className="text-xs text-slate-500">
                          {item.unit === 'job' ? 'Fixed price' : `${item.unit} x $${item.price}`}
                        </p>
                      </div>
                      <p className="text-lg font-black text-purple-600">${item.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Template List View
  const categories = ['intake', 'service', 'cleanup', 'safety'] as const;
  const categoryLabels: Record<string, string> = {
    intake: '📋 Intake Forms',
    service: '⚙️ Service Checklists',
    cleanup: '🧹 Cleanup & Closeout',
    safety: '🛡️ Safety Assessments',
  };

  // Template Builder Modal
  if (showBuilder) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Edit3 className="w-6 h-6 text-purple-600" />
            <h2 className="text-2xl font-black uppercase">Create New Template</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowBuilder(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Step Tabs */}
        <div className="flex gap-2 bg-slate-100 p-2 rounded-2xl">
          {(['details', 'checklist', 'fields', 'pricing'] as const).map(step => (
            <button
              key={step}
              onClick={() => setBuilderStep(step)}
              className={`flex-1 py-3 rounded-xl font-black uppercase text-[10px] transition-all ${
                builderStep === step
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {step}
            </button>
          ))}
        </div>

        {/* DETAILS STEP */}
        {builderStep === 'details' && (
          <Card className="border-2 border-slate-100 rounded-2xl p-6">
            <h3 className="text-lg font-black uppercase mb-4">Template Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black uppercase mb-1">Template Name</label>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="e.g. Morning Setup Checklist"
                  className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold focus:border-purple-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase mb-1">Category</label>
                <select
                  value={newTemplateCategory}
                  onChange={(e) => setNewTemplateCategory(e.target.value as any)}
                  className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold focus:border-purple-600 focus:outline-none"
                >
                  <option value="intake">📋 Intake Forms</option>
                  <option value="service">⚙️ Service Checklists</option>
                  <option value="cleanup">🧹 Cleanup & Closeout</option>
                  <option value="safety">🛡️ Safety Assessments</option>
                </select>
              </div>
            </div>
          </Card>
        )}

        {/* CHECKLIST STEP */}
        {builderStep === 'checklist' && (
          <Card className="border-2 border-slate-100 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black uppercase">Multi-Step Checklist</h3>
              <Button onClick={addStep} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Add Step
              </Button>
            </div>

            {/* Steps */}
            <div className="space-y-4 mb-4">
              {Array.from({ length: currentStepNum }, (_, i) => i + 1).map(stepNum => (
                <div key={stepNum} className="border-2 border-slate-100 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-black text-slate-900">Step {stepNum}</h4>
                    {stepNum > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStep(stepNum)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {newChecklistItems.filter(item => item.step === stepNum).map(item => (
                      <div key={item.id} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
                        <CheckSquare className="w-4 h-4 text-purple-600" />
                        <span className="flex-1 text-sm font-bold">{item.text}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeChecklistItem(item.id)}
                          className="h-6 w-6 p-0 text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      value={newChecklistInput}
                      onChange={(e) => setNewChecklistInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addChecklistItem()}
                      placeholder={`Add item to step ${stepNum}...`}
                      className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-sm focus:border-purple-600 focus:outline-none"
                    />
                    <Button onClick={addChecklistItem} size="sm">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Total items: {newChecklistItems.length}
            </p>
          </Card>
        )}

        {/* FIELDS STEP */}
        {builderStep === 'fields' && (
          <Card className="border-2 border-slate-100 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black uppercase">Custom Fields</h3>
              <div className="flex gap-2">
                <Button onClick={() => addField('text')} variant="outline" size="sm">Text</Button>
                <Button onClick={() => addField('number')} variant="outline" size="sm">Number</Button>
                <Button onClick={() => addField('textarea')} variant="outline" size="sm">Long Text</Button>
              </div>
            </div>

            {newFields.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No fields yet. Add some above!</p>
            ) : (
              <div className="space-y-3">
                {newFields.map((field, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-xl p-3 flex gap-2 items-center">
                    <span className="text-xs font-bold text-slate-400 w-6">{idx + 1}.</span>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(idx, { label: e.target.value })}
                      className="flex-1 h-8 rounded border border-slate-200 px-2 text-sm"
                      placeholder="Field label"
                    />
                    <select
                      value={field.type}
                      onChange={(e) => updateField(idx, { type: e.target.value })}
                      className="h-8 rounded border border-slate-200 px-2 text-xs"
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="textarea">Long Text</option>
                      <option value="select">Dropdown</option>
                      <option value="checkbox">Checkbox</option>
                    </select>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={field.required || false}
                        onChange={(e) => updateField(idx, { required: e.target.checked })}
                      />
                      Required
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeField(idx)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* PRICING STEP */}
        {builderStep === 'pricing' && (
          <Card className="border-2 border-slate-100 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black uppercase">Preset Quote Items</h3>
              <Button onClick={addQuoteItem} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>

            {newQuoteItems && newQuoteItems.length > 0 ? (
              <div className="space-y-3">
                {newQuoteItems.map((item: any, idx: number) => (
                  <div key={idx} className="border border-slate-200 rounded-xl p-3 grid grid-cols-12 gap-2 items-center">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateQuoteItem(idx, { description: e.target.value })}
                      placeholder="Description"
                      className="col-span-5 h-8 rounded border border-slate-200 px-2 text-sm"
                    />
                    <select
                      value={item.unit}
                      onChange={(e) => updateQuoteItem(idx, { unit: e.target.value })}
                      className="col-span-2 h-8 rounded border border-slate-200 px-2 text-xs"
                    >
                      <option value="job">Job</option>
                      <option value="hour">Hour</option>
                      <option value="visit">Visit</option>
                      <option value="service">Service</option>
                      <option value="room">Room</option>
                      <option value="vehicle">Vehicle</option>
                    </select>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateQuoteItem(idx, { quantity: parseInt(e.target.value) || 0 })}
                      placeholder="Qty"
                      className="col-span-2 h-8 rounded border border-slate-200 px-2 text-sm"
                    />
                    <input
                      type="number"
                      value={item.price}
                      onChange={(e) => updateQuoteItem(idx, { price: parseFloat(e.target.value) || 0 })}
                      placeholder="Price"
                      className="col-span-2 h-8 rounded border border-slate-200 px-2 text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeQuoteItem(idx)}
                      className="col-span-1 text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">No preset items. Add some above!</p>
            )}
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          {builderStep !== 'details' && (
            <Button
              variant="outline"
              onClick={() => setBuilderStep(prev => {
                const steps = ['details', 'checklist', 'fields', 'pricing'];
                const idx = steps.indexOf(prev);
                return steps[idx - 1] as any;
              })}
              className="flex-1 h-14 rounded-2xl"
            >
              Back
            </Button>
          )}
          {builderStep !== 'pricing' ? (
            <Button
              onClick={() => setBuilderStep(prev => {
                const steps = ['details', 'checklist', 'fields', 'pricing'];
                const idx = steps.indexOf(prev);
                return steps[idx + 1] as any;
              })}
              className="flex-1 h-14 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={saveNewTemplate}
              className="flex-1 h-14 bg-teal-500 hover:bg-teal-600 rounded-2xl font-black uppercase"
            >
              <Save className="w-5 h-5 mr-2" />
              Create Template
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-black uppercase">Task Templates</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase">
            {templates.length} template{templates.length !== 1 ? 's' : ''}
          </span>
          <Button onClick={startNewTemplate} size="sm" className="rounded-xl">
            <Plus className="w-4 h-4 mr-1" />
            New Template
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="font-bold text-sm">No task templates found for {config?.biz_type}</p>
          <p className="text-xs mt-1">Run database migrations to load templates</p>
        </div>
      ) : (
        <div className="space-y-6">
          {categories.map(category => {
            const categoryTemplates = templates.filter(t => t.template_category === category);
            if (categoryTemplates.length === 0) return null;

            return (
              <div key={category}>
                <h3 className="text-sm font-black uppercase text-slate-500 mb-3">
                  {categoryLabels[category]}
                </h3>
                <div className="grid gap-3">
                  {categoryTemplates.map(template => (
                    <div
                      key={template.id}
                      className={`bg-white border-2 rounded-2xl p-5 transition-all ${
                        template.is_active ? 'border-slate-100 hover:border-purple-300 hover:shadow-md' : 'border-slate-200 bg-slate-50 opacity-75'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="font-black text-slate-900 uppercase">{template.template_name}</h4>
                          {!template.is_active && (
                            <span className="text-[10px] font-bold text-red-600 uppercase bg-red-50 px-2 py-0.5 rounded mt-1 inline-block">
                              Disabled
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateTemplate(template);
                            }}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-purple-600"
                            title="Duplicate template"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTemplateActive(template);
                            }}
                            className={`h-8 w-8 p-0 ${
                              template.is_active ? 'text-slate-400 hover:text-red-600' : 'text-green-600'
                            }`}
                            title={template.is_active ? 'Disable template' : 'Enable template'}
                          >
                            {template.is_active ? <X className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      <button
                        onClick={() => openTemplate(template)}
                        className="w-full text-left"
                      >
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="text-[10px] font-bold text-slate-500 uppercase bg-slate-100 px-2 py-1 rounded">
                            {template.fields.length} fields
                          </span>
                          <span className="text-[10px] font-bold text-slate-500 uppercase bg-slate-100 px-2 py-1 rounded">
                            {template.checklist_items.length} checklist items
                          </span>
                          {template.preset_quote_items && template.preset_quote_items.length > 0 && (
                            <span className="text-[10px] font-bold text-teal-600 uppercase bg-teal-50 px-2 py-1 rounded flex items-center gap-1">
                              <ClipboardList className="w-3 h-3" />
                              {template.preset_quote_items.length} quote items
                            </span>
                          )}
                          {Object.keys(template.protective_data_points).length > 0 && (
                            <span className="text-[10px] font-bold text-amber-600 uppercase bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              Protected
                            </span>
                          )}
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
